import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  InputGroupText,
  FormFeedback,
  Collapse,
  CardHeader,
} from "reactstrap";
import { Formik, FieldArray, useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CourseService from "../../../services/KangarooServices/CourseService";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import MediaModal from "../../PagesSection/Component/MediaModal";
import MediaService from "../../../services/KangarooServices/MediaServices";
import { imageBaseApi } from "../../../utilis/imageHelper";
import PageService from "../../../services/KangarooServices/PageService";
import RichTextEditor from "../../../Components/Editor/RichTextEditor";
import uploadImageApi from "../../../utilis/uploadImage";
import coursevalidationSchema from "../../../validation/CourseValidation";
import QuizService from "../../../services/KangarooServices/QuizService";

const CourseCreate = () => {
  const { id } = useParams(); // present → update mode
  const navigate = useNavigate();

  const [activeTab] = useState("basic");
  const [selected, setSelected] = useState({ type: "basic" });
  const [bannerMedia, setBannerMedia] = useState(null);
  const [mediaData, setMediaData] = useState([]);
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [mediaTarget, setMediaTarget] = useState(null); // string or object
  const [grpData, setGrpData] = useState([]);

  const [slugMessage, setSlugMessage] = useState(""); // message text
  const [isSlugValid, setIsSlugValid] = useState(true); // validity
  const [initialData, setInitialData] = useState();
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedSubDetails, setExpandedSubDetails] = useState({});

  const [quizData, setQuizData] = useState([]);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const response = await QuizService.getList();
        setQuizData(response);
      } catch (error) {
        console.log(error);
      }
    };
    fetchQuiz();
  }, []);
  // ADD THIS: Ref to track if data has been initialized
  const hasInitialized = useRef(false);

  const toggleSection = (cIdx) => {
    setExpandedSections((prev) => ({
      ...prev,
      [cIdx]: !prev[cIdx],
    }));
  };

  const toggleSubDetail = (key) => {
    setExpandedSubDetails((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  /* ---------------- HELPERS ---------------- */
  const generateSlug = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-");

  const checkSlug = async (slug) => {
    if (!slug) return;
    try {
      const res = await CourseService.checkSlug({ slug });
      setIsSlugValid(!res.exists);
      setSlugMessage(
        res.exists
          ? `Slug taken. Suggested: ${res.suggestedSlug}`
          : "Slug available",
      );
    } catch {
      setIsSlugValid(false);
      setSlugMessage("Unable to check slug");
    }
  };

  // ────────────────────────────────────────────────
  //  Fetch media library once
  // ────────────────────────────────────────────────

  useEffect(() => {
    const fetchGrp = async () => {
      try {
        const res = await CourseService.getGrpList();
        setGrpData(res || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load media library");
      }
    };
    fetchGrp();
  }, []);

  // ────────────────────────────────────────────────
  //  Fetch course details for update mode - FIXED
  // ────────────────────────────────────────────────
  useEffect(() => {
    const fetchDetail = async () => {
      // FIXED: Only fetch once
      if (!id || hasInitialized.current) return;

      try {
        const res = await CourseService.getDetail(id);

        // Set banner media if exists
        if (res?.bannerMedia) {
          setBannerMedia(res.bannerMedia);
        }

        // Transform the response to match form structure
        const transformedData = {
          subName: res?.subName || "",
          name: res?.name || "",
          slug: res?.slug || "",
          orderIndex: res?.orderIndex || 0,
          courseCode: res?.courseCode || "",
          bannerMediaId: res?.bannerMedia?.id || null,
          coursesGroupId: res?.coursesGroupId || "",
          isPublished: res?.isPublished,
          coursesChildren: res?.children?.map((child) => ({
            isMainChild: child.isMainChild || false,
            hasSubMenu: child.hasSubMenu || false,
            heading: child.heading || "",
            isQuiz: child?.isQuiz || false,
            quizSlug: child?.quizSlug,
            orderIndex: child?.orderIndex || 0,
            description: child.description || "", // <-- FIXED typo here
            textFields:
              child.textFields?.map((tf) => ({
                heading: tf.heading || "",
                description: tf.description || "", // <-- FIXED typo here
                videoUrl: tf.videoUrl || "",
                isMainImage: tf.isMainImage || false,
                mediaId: tf.media?.id || null,
                orderIndex: tf.orderIndex || 0,
              })) || [],
            childrenSubDetails:
              child.childrenSubDetails?.map((sub) => ({
                orderIndex: sub.orderIndex || 0,
                heading: sub.heading || "",
                hasSubMenu: sub.hasSubMenu || false,
                isQuiz: sub?.isQuiz || false,
                description: sub?.description,
                videoUrl: sub?.videoUrl,
                quizSlug: sub?.quizSlug,
                textFields:
                  sub.textFields?.map((stf) => ({
                    heading: stf.heading || "",
                    description: stf.description || "", // <-- FIXED typo here
                    videoUrl: stf.videoUrl || "",
                    isMainImage: stf.isMainImage || false,
                    mediaId: stf.media?.id || null,
                    orderIndex: stf.orderIndex || 0,
                  })) || [],
              })) || [],
          })) || [
            {
              isMainChild: true,
              hasSubMenu: false,
              heading: "",
              description: "",
              orderIndex: 0,
              textFields: [
                {
                  heading: "",
                  description: "",
                  videoUrl: "",
                  isMainImage: true,
                  mediaId: null,
                  orderIndex: 0,
                },
              ],
              childrenSubDetails: [],
            },
          ],
        };

        setInitialData(transformedData);
        hasInitialized.current = true; // FIXED: Mark as initialized
      } catch (err) {
        console.error(err);
        toast.error("Failed to load course details");
      }
    };
    fetchDetail();
  }, [id]); // FIXED: Removed initialData from dependencies

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const res = await MediaService.getList({ page: 1, pageSize: 1000 });
        setMediaData(res?.items || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load media library");
      }
    };
    fetchMedia();
  }, []);

  const formik = useFormik({
    initialValues: initialData || {
      subName: "",
      name: "",
      slug: "",
      courseCode: "",
      bannerMediaId: "",
      orderIndex: 0,
      coursesGroupId: "",
      isPublished: true,
      coursesChildren: [
        {
          isMainChild: true,
          hasSubMenu: false,
          heading: "",
          description: "",
          isQuiz: false,
          orderIndex: 0,
          textFields: [
            {
              heading: "",
              description: "",
              videoUrl: "",
              isMainImage: true,
              mediaId: null,
              orderIndex: 0,
            },
          ],
          childrenSubDetails: [],
        },
      ],
    },
    enableReinitialize: true,
    keepDirtyOnReinitialize: true, // FIXED: Prevent resetting dirty fields
    validationSchema: coursevalidationSchema,

    onSubmit: async (values, { setSubmitting }) => {
      try {
        let response;
        if (id) {
          // UPDATE
          response = await CourseService.update(id, values);
          toast.success("Course updated successfully");
        } else {
          // CREATE
          response = await CourseService.create(values);
          toast.success("Course created successfully");
        }
        navigate("/courses");
      } catch (error) {
        console.error(error);
        toast.error(error?.message || "Operation failed");
      } finally {
        setSubmitting(false);
      }
    },
  });
  console.log("🚀 ~ CourseCreate ~ formik:", formik);

  // ────────────────────────────────────────────────
  //  Media selection logic
  // ────────────────────────────────────────────────
  const openMediaModal = (target) => {
    setMediaTarget(target);
    setIsMediaOpen(true);
  };

  const handleMediaSelect = (selectedMedia) => {
    if (!selectedMedia || !mediaTarget) return;

    // Banner
    if (mediaTarget === "banner") {
      setBannerMedia(selectedMedia);
      formik.setFieldValue("bannerMediaId", selectedMedia.id);
    }

    // TextField inside a child section
    else if (mediaTarget?.type === "textField") {
      const { childIdx, textIdx } = mediaTarget;
      formik.setFieldValue(
        `coursesChildren[${childIdx}].textFields[${textIdx}].mediaId`,
        selectedMedia.id,
      );
    }

    // TextField inside a sub-detail
    else if (mediaTarget?.type === "subTextField") {
      const { childIdx, subIdx, textIdx } = mediaTarget;
      formik.setFieldValue(
        `coursesChildren[${childIdx}].childrenSubDetails[${subIdx}].textFields[${textIdx}].mediaId`,
        selectedMedia.id,
      );
    }

    setIsMediaOpen(false);
    setMediaTarget(null);
  };
  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all required fields before submitting");
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
      );
      return;
    }
    const filterEmptyFields = (obj) => {
      if (Array.isArray(obj)) {
        return obj
          .map(filterEmptyFields)
          .filter(
            (item) =>
              item !== null &&
              item !== undefined &&
              (typeof item !== "object" || Object.keys(item).length > 0),
          );
      } else if (typeof obj === "object" && obj !== null) {
        const newObj = {};
        Object.entries(obj).forEach(([key, value]) => {
          const filteredValue = filterEmptyFields(value);
          if (
            filteredValue !== null &&
            filteredValue !== undefined &&
            !(
              typeof filteredValue === "object" &&
              Object.keys(filteredValue).length === 0
            )
          ) {
            newObj[key] = filteredValue;
          }
        });
        return Object.keys(newObj).length > 0 ? newObj : undefined;
      } else {
        return obj !== "" && obj !== null && obj !== undefined
          ? obj
          : undefined;
      }
    };
    const payload = filterEmptyFields(formik.values);
    try {
      let response;
      if (id) {
        response = await CourseService.update(id, payload);
        toast.success("Course updated successfully");
      } else {
        response = await CourseService.create(payload);
        toast.success("Course created successfully");
      }
      navigate("/courses");
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Operation failed");
    }
  };

  return (
    <div style={{ backgroundColor: "#f8f9fa" }} className="page-content">
      <Container fluid>
        <BreadCrumb
          title={id ? "Edit Course" : "Add Course"}
          pageTitle="Courses"
          breadcrumbItems={[{ title: "Back to List", link: "/courses" }]}
        />

        <Row>
          <Card
            style={{
              border: "none",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <CardBody className="">
              <div className="mb-4">
                <h3 style={{ color: "#7b2d8e", fontWeight: "bold" }}>
                  {id ? "Edit Course" : "Add New Course"}
                </h3>
                <p className="text-muted">Fill in the course details below</p>
              </div>

              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  {/* Top bar */}
                  <div className="cms-topbar mb-4">
                    <strong>{formik.values.name || "New Course"}</strong>
                    <div>
                      <Button
                        outline
                        type="button"
                        className="me-2"
                        color="danger"
                        onClick={() => navigate("/courses")}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="success"
                        onClick={handleSubmit}
                        disabled={formik.isSubmitting}
                      >
                        {formik.isSubmitting
                          ? "Saving..."
                          : id
                          ? "Update"
                          : "Create"}
                      </Button>
                    </div>
                  </div>

                  <Row>
                    {/* Sidebar – tree structure */}
                    <Col md={4} className="cms-sidebar rounded-4">
                      <Nav vertical>
                        {/* BASIC INFO */}
                        <div
                          className={`cms-item cms-section ${
                            selected.type === "basic" ? "active" : ""
                          }`}
                          onClick={() => setSelected({ type: "basic" })}
                        >
                          <div className="cms-left">
                            <i className="ri-information-line cms-icon" />
                            <span>Basic Information</span>
                          </div>
                        </div>

                        <FieldArray name="coursesChildren">
                          {({ push, remove }) => (
                            <>
                              {/* ADD SECTION */}
                              <Button
                                size="sm"
                                outline
                                color="primary"
                                className="w-100 mb-2"
                                onClick={() =>
                                  push({
                                    isMainChild: false,
                                    hasSubMenu: false,
                                    heading: "",
                                    orderIndex: 0,
                                    description: "",
                                    textFields: [
                                      // {
                                      //   heading: "",
                                      //   description: "",
                                      //   videoUrl: "",
                                      //   isMainImage: false,
                                      //   mediaId: null,
                                      // },
                                    ],
                                    childrenSubDetails: [],
                                  })
                                }
                              >
                                + Add Section
                              </Button>

                              {formik.values.coursesChildren.map(
                                (child, cIdx) => {
                                  const isExpanded =
                                    expandedSections[cIdx] !== false;
                                  const sectionKey = `section-${cIdx}`;

                                  const basePath = `coursesChildren.${cIdx}`;
                                  return (
                                    <div key={cIdx}>
                                      {/* SECTION */}
                                      <div
                                        className={`cms-item cms-section ${
                                          selected.type === "child" &&
                                          selected.cIdx === cIdx
                                            ? "active"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          setSelected({ type: "child", cIdx });
                                          toggleSection(cIdx);
                                        }}
                                      >
                                        <div className="cms-left">
                                          <i
                                            className={`ri-arrow-right-s-line ${
                                              isExpanded ? "rotate" : ""
                                            }`}
                                          />
                                          <i className="ri-folder-line cms-icon" />
                                          <span className="truncate-text">
                                            {child.heading ||
                                              `Section ${cIdx + 1}`}
                                          </span>
                                        </div>

                                        <div className="d-flex align-content-center justify-content-center">
                                          {/* + Add Text button */}
                                          <Button
                                            size="sm"
                                            outline
                                            color="success"
                                            className="me-1"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const textFieldsPath = `coursesChildren.${cIdx}.textFields`;
                                              formik.setFieldValue(
                                                textFieldsPath,
                                                [
                                                  ...child.textFields,
                                                  {
                                                    heading: "",
                                                    description: "",
                                                    videoUrl: "",
                                                    isMainImage: false,
                                                    mediaId: null,
                                                    orderIndex: 0,
                                                  },
                                                ],
                                              );
                                            }}
                                          >
                                            + Tx
                                          </Button>

                                          {/* + Add Sub Detail button */}
                                          <Button
                                            size="sm"
                                            color="success"
                                            className="me-1"
                                            onClick={(e) => {
                                              e.stopPropagation();

                                              const subDetailsPath = `coursesChildren.${cIdx}.childrenSubDetails`;
                                              const hasSubMenuPath = `coursesChildren.${cIdx}.hasSubMenu`;

                                              // 1️⃣ Set hasSubMenu = true for section
                                              formik.setFieldValue(
                                                hasSubMenuPath,
                                                true,
                                              );

                                              // 2️⃣ Push new sub detail
                                              formik.setFieldValue(
                                                subDetailsPath,
                                                [
                                                  ...(child.childrenSubDetails ||
                                                    []),
                                                  {
                                                    orderIndex:
                                                      child.childrenSubDetails
                                                        .length,
                                                    heading: "",
                                                    hasSubMenu: false,
                                                    isQuiz: false,
                                                    quizSlug: "",
                                                    textFields: [],
                                                  },
                                                ],
                                              );
                                            }}
                                          >
                                            + S.D
                                          </Button>
                                          {!child.isQuiz && (
                                            <Button
                                              size="sm"
                                              color="success"
                                              className="me-1"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                formik.setFieldValue(
                                                  `${basePath}.isQuiz`,
                                                  true,
                                                );
                                                formik.setFieldValue(
                                                  `${basePath}.quizSlug`,
                                                  "",
                                                );
                                              }}
                                            >
                                              + Quiz
                                            </Button>
                                          )}

                                          {/* Remove Section */}
                                          <Button
                                            size="sm"
                                            color="danger"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              remove(cIdx);
                                            }}
                                          >
                                            ×
                                          </Button>
                                        </div>
                                      </div>

                                      <Collapse isOpen={isExpanded}>
                                        {/* TEXT BLOCKS */}

                                        <FieldArray
                                          name={`coursesChildren.${cIdx}.textFields`}
                                        >
                                          {({ remove: removeText }) => (
                                            <>
                                              {child.textFields.map(
                                                (tf, tfIdx) => (
                                                  <div
                                                    key={tfIdx}
                                                    className={`cms-item cms-text ${
                                                      selected.type ===
                                                        "childText" &&
                                                      selected.cIdx === cIdx &&
                                                      selected.tfIdx === tfIdx
                                                        ? "active"
                                                        : ""
                                                    }`}
                                                    onClick={() =>
                                                      setSelected({
                                                        type: "childText",
                                                        cIdx,
                                                        tfIdx,
                                                      })
                                                    }
                                                  >
                                                    <div className="cms-left">
                                                      <i className="ri-file-text-line cms-icon" />
                                                      <span>
                                                        {tf.heading ||
                                                          `Text ${tfIdx + 1}`}
                                                      </span>
                                                    </div>

                                                    <Button
                                                      size="sm"
                                                      color="danger"
                                                      className="cms-remove"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeText(tfIdx);
                                                      }}
                                                    >
                                                      ×
                                                    </Button>
                                                  </div>
                                                ),
                                              )}
                                            </>
                                          )}
                                        </FieldArray>

                                        {/* SUB DETAILS */}
                                        <FieldArray
                                          name={`coursesChildren.${cIdx}.childrenSubDetails`}
                                        >
                                          {({ remove: removeSub }) => (
                                            <>
                                              {child.childrenSubDetails.map(
                                                (sub, sdIdx) => {
                                                  const key = `${sectionKey}-${cIdx}-${sdIdx}`;
                                                  const isSubExpanded =
                                                    expandedSubDetails[key] !==
                                                    false;
                                                  const basePath = `coursesChildren.${cIdx}.childrenSubDetails.${sdIdx}`;

                                                  return (
                                                    <div key={key}>
                                                      {/* ================= Sub Detail Header ================= */}
                                                      <div
                                                        className={`cms-item cms-sub ${
                                                          selected.type ===
                                                            "subDetail" &&
                                                          selected.cIdx ===
                                                            cIdx &&
                                                          selected.sdIdx ===
                                                            sdIdx
                                                            ? "active"
                                                            : ""
                                                        }`}
                                                        onClick={() => {
                                                          setSelected({
                                                            type: "subDetail",
                                                            cIdx,
                                                            sdIdx,
                                                          });
                                                          toggleSubDetail(key);
                                                        }}
                                                      >
                                                        <div className="cms-left">
                                                          <i
                                                            className={`ri-arrow-right-s-line ${
                                                              isSubExpanded
                                                                ? "rotate"
                                                                : ""
                                                            }`}
                                                          />
                                                          <i className="ri-bookmark-line cms-icon" />
                                                          <span>
                                                            {sub.heading ||
                                                              `Sub ${
                                                                sdIdx + 1
                                                              }`}
                                                          </span>
                                                        </div>

                                                        <div className="cms-right">
                                                          {/* + Add Text */}
                                                          <Button
                                                            size="sm"
                                                            color="success"
                                                            className="me-1"
                                                            onClick={(e) => {
                                                              e.stopPropagation();

                                                              const textFieldsPath = `${basePath}.textFields`;
                                                              const hasSubMenuPath = `${basePath}.hasSubMenu`;

                                                              // 1️⃣ Set subDetail hasSubMenu = true
                                                              formik.setFieldValue(
                                                                hasSubMenuPath,
                                                                true,
                                                              );

                                                              // 2️⃣ Add new text field
                                                              formik.setFieldValue(
                                                                textFieldsPath,
                                                                [
                                                                  ...(sub.textFields ||
                                                                    []),
                                                                  {
                                                                    heading: "",
                                                                    description:
                                                                      "",
                                                                    videoUrl:
                                                                      "",
                                                                    orderIndex: 0,
                                                                    isMainImage: false,
                                                                    mediaId:
                                                                      null,
                                                                  },
                                                                ],
                                                              );
                                                            }}
                                                          >
                                                            + Tx
                                                          </Button>

                                                          {/* + Add Quiz (only if not exists) */}
                                                          {!sub.isQuiz && (
                                                            <Button
                                                              size="sm"
                                                              color="success"
                                                              className="me-1"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                formik.setFieldValue(
                                                                  `${basePath}.isQuiz`,
                                                                  true,
                                                                );
                                                                formik.setFieldValue(
                                                                  `${basePath}.quizSlug`,
                                                                  "",
                                                                );
                                                              }}
                                                            >
                                                              + Quiz
                                                            </Button>
                                                          )}

                                                          {/* Remove Sub Detail */}
                                                          <Button
                                                            size="sm"
                                                            color="danger"
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              removeSub(sdIdx);
                                                            }}
                                                          >
                                                            ×
                                                          </Button>
                                                        </div>
                                                      </div>

                                                      {/* ================= Collapse Area ================= */}
                                                      <Collapse
                                                        isOpen={isSubExpanded}
                                                      >
                                                        {/* ----------- Quiz Node ----------- */}
                                                        {sub.isQuiz && (
                                                          <div
                                                            className="cms-item cms-subnode-inner"
                                                            onClick={() =>
                                                              setSelected({
                                                                type: "subDetailQuiz",
                                                                cIdx,
                                                                sdIdx,
                                                              })
                                                            }
                                                          >
                                                            <div className="cms-left">
                                                              <i className="ri-questionnaire-line cms-icon" />
                                                              <span className="truncate-text">
                                                                {sub.quizSlug ||
                                                                  "Quiz"}
                                                              </span>
                                                            </div>

                                                            <Button
                                                              size="sm"
                                                              color="danger"
                                                              className="cms-remove"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                formik.setFieldValue(
                                                                  `${basePath}.isQuiz`,
                                                                  false,
                                                                );
                                                                formik.setFieldValue(
                                                                  `${basePath}.quizSlug`,
                                                                  "",
                                                                );
                                                              }}
                                                            >
                                                              ×
                                                            </Button>
                                                          </div>
                                                        )}

                                                        {/* ----------- Text Fields ----------- */}
                                                        <FieldArray
                                                          name={`${basePath}.textFields`}
                                                        >
                                                          {({
                                                            remove:
                                                              removeSubText,
                                                          }) => (
                                                            <>
                                                              {(
                                                                sub.textFields ||
                                                                []
                                                              ).map(
                                                                (
                                                                  stf,
                                                                  stfIdx,
                                                                ) => (
                                                                  <div
                                                                    key={`${cIdx}-${sdIdx}-${stfIdx}`}
                                                                    className="cms-item cms-subnode-inner"
                                                                    onClick={() =>
                                                                      setSelected(
                                                                        {
                                                                          type: "subDetailText",
                                                                          cIdx,
                                                                          sdIdx,
                                                                          stfIdx,
                                                                        },
                                                                      )
                                                                    }
                                                                  >
                                                                    <div className="cms-left">
                                                                      <i className="ri-file-text-line cms-icon" />
                                                                      <span className="truncate-text">
                                                                        {stf.heading ||
                                                                          `Text ${
                                                                            stfIdx +
                                                                            1
                                                                          }`}
                                                                      </span>
                                                                    </div>

                                                                    <Button
                                                                      size="sm"
                                                                      color="danger"
                                                                      className="cms-remove"
                                                                      onClick={(
                                                                        e,
                                                                      ) => {
                                                                        e.stopPropagation();
                                                                        removeSubText(
                                                                          stfIdx,
                                                                        );
                                                                      }}
                                                                    >
                                                                      ×
                                                                    </Button>
                                                                  </div>
                                                                ),
                                                              )}
                                                            </>
                                                          )}
                                                        </FieldArray>
                                                      </Collapse>
                                                    </div>
                                                  );
                                                },
                                              )}
                                            </>
                                          )}
                                        </FieldArray>
                                        {child.isQuiz && (
                                          <div
                                            className="cms-item cms-subnode-inner"
                                            onClick={() =>
                                              setSelected({
                                                type: "childQuiz",
                                                cIdx,
                                              })
                                            }
                                          >
                                            <div className="cms-left">
                                              <i className="ri-questionnaire-line cms-icon" />
                                              <span className="truncate-text">
                                                {child.quizSlug || "Quiz"}
                                              </span>
                                            </div>

                                            <Button
                                              size="sm"
                                              color="danger"
                                              className="cms-remove"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                formik.setFieldValue(
                                                  `${basePath}.isQuiz`,
                                                  false,
                                                );
                                                formik.setFieldValue(
                                                  `${basePath}.quizSlug`,
                                                  "",
                                                );
                                              }}
                                            >
                                              ×
                                            </Button>
                                          </div>
                                        )}
                                      </Collapse>
                                    </div>
                                  );
                                },
                              )}
                            </>
                          )}
                        </FieldArray>
                      </Nav>
                    </Col>

                    {/* Main editor area */}
                    <Col md={8} className="cms-editor">
                      {/* ── BASIC ── */}
                      {selected.type === "basic" && (
                        <Card>
                          <CardBody>
                            <h5>Basic Information</h5>
                            <div md={12} className="mb-2">
                              <Label>Course Group</Label>
                              <Input
                                type="select"
                                value={formik.values.coursesGroupId}
                                onChange={formik.handleChange}
                                name="coursesGroupId"
                              >
                                <option value="">Select</option>
                                {grpData.map((d) => (
                                  <option key={d.id} value={d.id}>
                                    {d.headingName}
                                  </option>
                                ))}
                              </Input>
                            </div>
                            <FormGroup className="mb-3">
                              <Label htmlFor="name">
                                Name <span className="text-danger">*</span>
                              </Label>

                              <Input
                                value={formik.values.name}
                                onChange={(e) => {
                                  formik.setFieldValue("name", e.target.value);

                                  const slug = generateSlug(e.target.value);
                                  formik.setFieldValue("slug", slug);
                                  checkSlug(slug);
                                }}
                                invalid={
                                  formik.touched.name && !!formik.errors.name
                                }
                              />
                              {formik.touched.name && formik.errors.name && (
                                <FormFeedback>
                                  {formik.errors.name}
                                </FormFeedback>
                              )}
                            </FormGroup>
                            <FormGroup className="mb-3">
                              <Label htmlFor="slug">
                                Slug <span className="text-danger">*</span>
                              </Label>
                              <Input
                                // invalid={!isSlugValid}
                                value={formik.values.slug}
                                onBlur={(e) => checkSlug(e.target.value)}
                                onChange={(e) =>
                                  formik.setFieldValue("slug", e.target.value)
                                }
                                invalid={
                                  !isSlugValid ||
                                  (formik.touched.slug && !!formik.errors.slug)
                                }
                              />
                              <small
                                className={
                                  isSlugValid ? "text-success" : "text-danger"
                                }
                              >
                                {slugMessage}
                              </small>
                              {formik.touched.slug && formik.errors.slug && (
                                <FormFeedback>
                                  {formik.errors.slug}
                                </FormFeedback>
                              )}
                            </FormGroup>
                            <div className="mb-3">
                              <Label>Sub Name</Label>
                              <Input
                                name="subName"
                                value={formik.values.subName}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            </div>
                            <div className="mb-3">
                              <Label>Order Index</Label>
                              <Input
                                type="number"
                                name="orderIndex"
                                value={formik.values.orderIndex}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              />
                            </div>
                            <div className="mb-3">
                              <Label>Course Code *</Label>
                              <Input
                                name="courseCode"
                                value={formik.values.courseCode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                invalid={
                                  formik.touched.courseCode &&
                                  !!formik.errors.courseCode
                                }
                              />
                              {formik.touched.courseCode &&
                                formik.errors.courseCode && (
                                  <FormFeedback>
                                    {formik.errors.courseCode}
                                  </FormFeedback>
                                )}
                            </div>

                            <div className="mb-3">
                              <Label>Banner Image</Label>
                              {bannerMedia ? (
                                <div className="border rounded p-3 bg-light text-center">
                                  <img
                                    src={`${imageBaseApi}${bannerMedia.url}`}
                                    alt="Banner"
                                    style={{
                                      maxHeight: "220px",
                                      objectFit: "contain",
                                    }}
                                    className="img-fluid mb-2"
                                  />
                                  <div className="fw-bold">
                                    {bannerMedia.fileName}
                                  </div>
                                  <Button
                                    size="sm"
                                    outline
                                    color="primary"
                                    className="me-2"
                                    onClick={() => openMediaModal("banner")}
                                  >
                                    Change
                                  </Button>
                                  <Button
                                    size="sm"
                                    outline
                                    color="danger"
                                    onClick={() => {
                                      setBannerMedia(null);
                                      formik.setFieldValue("bannerMediaId", "");
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ) : (
                                <div className="p-4 border rounded text-center bg-light">
                                  <i className="ri-image-add-line fs-1 text-secondary"></i>
                                  <h6 className="mt-2 mb-3 text-muted">
                                    No banner image selected
                                  </h6>
                                  <Button
                                    outline
                                    color="secondary"
                                    onClick={() => openMediaModal("banner")}
                                  >
                                    <i className="ri-image-add-line me-1"></i>{" "}
                                    Choose Banner Image
                                  </Button>
                                </div>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      )}

                      {/* ── SECTION (child) ── */}
                      {selected.type === "child" &&
                        selected.cIdx !== undefined && (
                          <Card>
                            <CardBody>
                              <h5>Section</h5>
                              <div className="mb-3">
                                <Label>Heading</Label>
                                <Input
                                  name={`coursesChildren.${selected.cIdx}.heading`}
                                  value={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.heading || ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  invalid={
                                    formik.touched.coursesChildren?.[
                                      selected.cIdx
                                    ]?.heading &&
                                    !!formik.errors.coursesChildren?.[
                                      selected.cIdx
                                    ]?.heading
                                  }
                                />
                                {formik.touched.coursesChildren?.[selected.cIdx]
                                  ?.heading &&
                                  formik.errors.coursesChildren?.[selected.cIdx]
                                    ?.heading && (
                                    <FormFeedback>
                                      {
                                        formik.errors.coursesChildren[
                                          selected.cIdx
                                        ].heading
                                      }
                                    </FormFeedback>
                                  )}
                              </div>
                              <div className="mb-3">
                                <Label>Order Index</Label>
                                <Input
                                  type="number"
                                  name={`coursesChildren.${selected.cIdx}.orderIndex`}
                                  value={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.orderIndex ?? ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                              </div>
                              <div className="mb-3">
                                <Label>Description</Label>

                                <RichTextEditor
                                  formik={formik}
                                  fieldName={`coursesChildren.${selected.cIdx}.description`}
                                  uploadImageApi={uploadImageApi}
                                />
                              </div>

                              <div className="form-check mb-3">
                                <Input
                                  type="checkbox"
                                  name={`coursesChildren.${selected.cIdx}.isMainChild`}
                                  checked={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.isMainChild || false
                                  }
                                  onChange={formik.handleChange}
                                />
                                <Label check>Is Main Section</Label>
                              </div>
                              {/* <div className="form-check">
                                <Input
                                  type="checkbox"
                                  name={`coursesChildren.${selected.cIdx}.hasSubMenu`}
                                  checked={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.hasSubMenu || false
                                  }
                                  onChange={formik.handleChange}
                                />
                                <Label check>Has Sub Menu</Label>
                              </div> */}
                            </CardBody>
                          </Card>
                        )}
                      {selected.type === "childQuiz" &&
                        selected.cIdx !== undefined && (
                          <Card className="h-100">
                            <CardHeader>
                              <h5>Child Quiz</h5>
                            </CardHeader>
                            <CardBody>
                              <Row className="mt-3">
                                {quizData?.map((item, index) => {
                                  // Get current selected quizSlug for this subDetail
                                  const currentSlug =
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.quizSlug;

                                  return (
                                    <Col key={index} lg={3}>
                                      <Card
                                        onClick={() => {
                                          // Make sure the indexes exist
                                          if (
                                            formik.values.coursesChildren[
                                              selected.cIdx
                                            ]
                                          ) {
                                            formik.setFieldValue(
                                              `coursesChildren.${selected.cIdx}.quizSlug`,
                                              item.slug,
                                            );
                                          }
                                        }}
                                        className="mb-2 h-100"
                                        style={{
                                          cursor: "pointer",
                                          border:
                                            currentSlug === item.slug
                                              ? "2px solid #7b2d8e"
                                              : "1px solid #ccc",
                                          backgroundColor:
                                            currentSlug === item.slug
                                              ? "#f0e6fa"
                                              : "#fff",
                                        }}
                                      >
                                        <CardBody>{item.quizName}</CardBody>
                                      </Card>
                                    </Col>
                                  );
                                })}
                              </Row>
                            </CardBody>
                          </Card>
                        )}

                      {/* ── TEXT BLOCK (inside section) ── */}
                      {selected.type === "childText" &&
                        selected.cIdx !== undefined &&
                        selected.tfIdx !== undefined && (
                          <Card>
                            <CardBody>
                              <h5>Text Block</h5>
                              <div className="mb-3">
                                <Label>Heading</Label>
                                <Input
                                  name={`coursesChildren.${selected.cIdx}.textFields.${selected.tfIdx}.heading`}
                                  value={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.textFields[selected.tfIdx]?.heading ||
                                    ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  invalid={
                                    formik.touched.coursesChildren?.[
                                      selected.cIdx
                                    ]?.textFields?.[selected.tfIdx]?.heading &&
                                    !!formik.errors.coursesChildren?.[
                                      selected.cIdx
                                    ]?.textFields?.[selected.tfIdx]?.heading
                                  }
                                />
                                {formik.touched.coursesChildren?.[selected.cIdx]
                                  ?.textFields?.[selected.tfIdx]?.heading &&
                                  formik.errors.coursesChildren?.[selected.cIdx]
                                    ?.textFields?.[selected.tfIdx]?.heading && (
                                    <FormFeedback>
                                      {
                                        formik.errors.coursesChildren[
                                          selected.cIdx
                                        ].textFields[selected.tfIdx].heading
                                      }
                                    </FormFeedback>
                                  )}
                              </div>
                              <Row>
                                <Col lg={4}>
                                  <div className="mb-3">
                                    <Label>Order Index</Label>
                                    <Input
                                      type="number"
                                      name={`coursesChildren.${selected.cIdx}.textFields.${selected.tfIdx}.orderIndex`}
                                      value={
                                        formik.values.coursesChildren[
                                          selected.cIdx
                                        ]?.textFields[selected.tfIdx]
                                          ?.orderIndex ?? ""
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                </Col>
                                <Col lg={8}>
                                  <div className="mb-3">
                                    <Label>Video URL</Label>
                                    <Input
                                      name={`coursesChildren.${selected.cIdx}.textFields.${selected.tfIdx}.videoUrl`}
                                      value={
                                        formik.values.coursesChildren[
                                          selected.cIdx
                                        ]?.textFields[selected.tfIdx]
                                          ?.videoUrl || ""
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                </Col>
                              </Row>
                              <div className="mb-3">
                                <Label>Description</Label>

                                <RichTextEditor
                                  formik={formik}
                                  fieldName={`coursesChildren.${selected.cIdx}.textFields.${selected.tfIdx}.description`}
                                />
                              </div>

                              <div className="mb-3">
                                <Label>Media (Image)</Label>
                                {formik.values.coursesChildren[selected.cIdx]
                                  ?.textFields[selected.tfIdx]?.mediaId ? (
                                  <>
                                    <div className="mb-2 text-center">
                                      <img
                                        src={`${imageBaseApi}${
                                          mediaData.find(
                                            (m) =>
                                              m.id ===
                                              formik.values.coursesChildren[
                                                selected.cIdx
                                              ].textFields[selected.tfIdx]
                                                .mediaId,
                                          )?.url
                                        }`}
                                        alt="Selected"
                                        style={{
                                          maxHeight: "150px",
                                          objectFit: "contain",
                                        }}
                                        className="img-fluid rounded"
                                      />
                                    </div>
                                    <div className="d-flex justify-content-center gap-4">
                                      <Button
                                        outline
                                        size="sm"
                                        onClick={() =>
                                          openMediaModal({
                                            type: "textField",
                                            childIdx: selected.cIdx,
                                            textIdx: selected.tfIdx,
                                          })
                                        }
                                      >
                                        {formik.values.coursesChildren[
                                          selected.cIdx
                                        ]?.textFields[selected.tfIdx]?.mediaId
                                          ? "Change Image"
                                          : "Select Image"}
                                      </Button>

                                      {formik.values.coursesChildren[
                                        selected.cIdx
                                      ]?.textFields[selected.tfIdx]
                                        ?.mediaId && (
                                        <Button
                                          size="sm"
                                          outline
                                          color="danger"
                                          onClick={() => {
                                            formik.setFieldValue(
                                              `coursesChildren.${selected.cIdx}.textFields.${selected.tfIdx}.mediaId`,
                                              "",
                                            );
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="p-4 border rounded text-center bg-light">
                                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                                      <h6 className="mt-2 mb-3 text-muted">
                                        No media selected
                                      </h6>
                                      <Button
                                        outline
                                        color="secondary"
                                        onClick={() =>
                                          openMediaModal({
                                            type: "textField",
                                            childIdx: selected.cIdx,
                                            textIdx: selected.tfIdx,
                                          })
                                        }
                                      >
                                        <i className="ri-image-add-line me-1"></i>{" "}
                                        Choose Banner Media
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* <div className="form-check">
                                <Input
                                  type="checkbox"
                                  name={`coursesChildren.${selected.cIdx}.textFields.${selected.tfIdx}.isMainImage`}
                                  checked={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.textFields[selected.tfIdx]
                                      ?.isMainImage || false
                                  }
                                  onChange={formik.handleChange}
                                />
                                <Label check>Main Image</Label>
                              </div> */}
                            </CardBody>
                          </Card>
                        )}

                      {/* ── SUB DETAIL ── */}
                      {selected.type === "subDetail" &&
                        selected.cIdx !== undefined &&
                        selected.sdIdx !== undefined && (
                          <Card>
                            <CardBody>
                              <h5>Sub Detail</h5>
                              <div className="mb-3">
                                <Label>Order Index</Label>
                                <Input
                                  type="number"
                                  name={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.orderIndex`}
                                  value={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.childrenSubDetails[selected.sdIdx]
                                      ?.orderIndex ?? ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                              </div>
                              <div className="mb-3">
                                <Label>Heading</Label>
                                <Input
                                  name={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.heading`}
                                  value={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.childrenSubDetails[selected.sdIdx]
                                      ?.heading || ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  invalid={
                                    formik.touched.coursesChildren?.[
                                      selected.cIdx
                                    ]?.childrenSubDetails?.[selected.sdIdx]
                                      ?.heading &&
                                    !!formik.errors.coursesChildren?.[
                                      selected.cIdx
                                    ]?.childrenSubDetails?.[selected.sdIdx]
                                      ?.heading
                                  }
                                />
                                {formik.touched.coursesChildren?.[selected.cIdx]
                                  ?.childrenSubDetails?.[selected.sdIdx]
                                  ?.heading &&
                                  formik.errors.coursesChildren?.[selected.cIdx]
                                    ?.childrenSubDetails?.[selected.sdIdx]
                                    ?.heading && (
                                    <FormFeedback>
                                      {
                                        formik.errors.coursesChildren[
                                          selected.cIdx
                                        ].childrenSubDetails[selected.sdIdx]
                                          .heading
                                      }
                                    </FormFeedback>
                                  )}
                              </div>
                              <div className="mb-3">
                                <Label>Video URL</Label>
                                <Input
                                  name={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.videoUrl`}
                                  value={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.childrenSubDetails[selected.sdIdx]
                                      ?.videoUrl || ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                              </div>
                              <div className="mb-3">
                                <Label>Description</Label>

                                <RichTextEditor
                                  formik={formik}
                                  fieldName={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.description`}
                                />
                              </div>
                              {/* <div className="form-check">
                                <Input
                                  type="checkbox"
                                  name={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.hasSubMenu`}
                                  checked={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.childrenSubDetails[selected.sdIdx]
                                      ?.hasSubMenu || false
                                  }
                                  onChange={formik.handleChange}
                                />
                                <Label check>Has Sub Menu</Label>
                              </div> */}
                            </CardBody>
                          </Card>
                        )}
                      {selected.type === "subDetailQuiz" &&
                        selected.cIdx !== undefined &&
                        selected.sdIdx !== undefined && (
                          <Card className="h-100">
                            <CardHeader>
                              <h5>Sub Detail Quiz</h5>
                            </CardHeader>
                            <CardBody>
                              <Row className="">
                                {quizData?.map((item, index) => {
                                  // Get current selected quizSlug for this subDetail
                                  const currentSlug =
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.childrenSubDetails[selected.sdIdx]
                                      ?.quizSlug;

                                  return (
                                    <Col key={index} lg={3}>
                                      <Card
                                        onClick={() => {
                                          // Make sure the indexes exist
                                          if (
                                            formik.values.coursesChildren[
                                              selected.cIdx
                                            ] &&
                                            formik.values.coursesChildren[
                                              selected.cIdx
                                            ].childrenSubDetails[selected.sdIdx]
                                          ) {
                                            formik.setFieldValue(
                                              `coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.quizSlug`,
                                              item.slug,
                                            );
                                          }
                                        }}
                                        className="mb-2 h-100"
                                        style={{
                                          cursor: "pointer",
                                          border:
                                            currentSlug === item.slug
                                              ? "2px solid #7b2d8e"
                                              : "1px solid #ccc",
                                          backgroundColor:
                                            currentSlug === item.slug
                                              ? "#f0e6fa"
                                              : "#fff",
                                        }}
                                      >
                                        <CardBody>{item.quizName}</CardBody>
                                      </Card>
                                    </Col>
                                  );
                                })}
                              </Row>
                            </CardBody>
                          </Card>
                        )}

                      {/* ── SUB DETAIL TEXT BLOCK ── */}
                      {selected.type === "subDetailText" &&
                        selected.cIdx !== undefined &&
                        selected.sdIdx !== undefined &&
                        selected.stfIdx !== undefined && (
                          <Card>
                            <CardBody>
                              <h5>Sub Detail – Text Block</h5>
                              <div className="mb-3">
                                <Label>Heading</Label>
                                <Input
                                  name={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.textFields.${selected.stfIdx}.heading`}
                                  value={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.childrenSubDetails[selected.sdIdx]
                                      ?.textFields?.[selected.stfIdx]
                                      ?.heading || ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  invalid={
                                    formik.touched.coursesChildren?.[
                                      selected.cIdx
                                    ]?.childrenSubDetails?.[selected.sdIdx]
                                      ?.textFields?.[selected.stfIdx]
                                      ?.heading &&
                                    !!formik.errors.coursesChildren?.[
                                      selected.cIdx
                                    ]?.childrenSubDetails?.[selected.sdIdx]
                                      ?.textFields?.[selected.stfIdx]?.heading
                                  }
                                />
                                {formik.touched.coursesChildren?.[selected.cIdx]
                                  ?.childrenSubDetails?.[selected.sdIdx]
                                  ?.textFields?.[selected.stfIdx]?.heading &&
                                  formik.errors.coursesChildren?.[selected.cIdx]
                                    ?.childrenSubDetails?.[selected.sdIdx]
                                    ?.textFields?.[selected.stfIdx]
                                    ?.heading && (
                                    <FormFeedback>
                                      {
                                        formik.errors.coursesChildren[
                                          selected.cIdx
                                        ].childrenSubDetails[selected.sdIdx]
                                          .textFields[selected.stfIdx].heading
                                      }
                                    </FormFeedback>
                                  )}
                              </div>
                              <Row>
                                <Col lg={4}>
                                  <div className="mb-3 ">
                                    <Label>Order Index</Label>
                                    <Input
                                      type="number"
                                      name={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.textFields.${selected.stfIdx}.orderIndex`}
                                      value={
                                        formik.values.coursesChildren[
                                          selected.cIdx
                                        ]?.childrenSubDetails[selected.sdIdx]
                                          ?.textFields[selected.stfIdx]
                                          ?.orderIndex ?? ""
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                </Col>
                                <Col lg={8}>
                                  <div className="mb-3 ">
                                    <Label>Video URL</Label>
                                    <Input
                                      name={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.textFields.${selected.stfIdx}.videoUrl`}
                                      value={
                                        formik.values.coursesChildren[
                                          selected.cIdx
                                        ]?.childrenSubDetails[selected.sdIdx]
                                          ?.textFields[selected.stfIdx]
                                          ?.videoUrl || ""
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                    />
                                  </div>
                                </Col>
                              </Row>

                              <div className="mb-3">
                                <Label>Description</Label>

                                <RichTextEditor
                                  formik={formik}
                                  fieldName={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.textFields.${selected.stfIdx}.description`}
                                />
                              </div>

                              <div className="mb-3">
                                <Label>Media (Image)</Label>
                                {formik.values.coursesChildren[selected.cIdx]
                                  ?.childrenSubDetails[selected.sdIdx]
                                  ?.textFields[selected.stfIdx]?.mediaId ? (
                                  <>
                                    <div className="mb-2 text-center">
                                      <img
                                        src={`${imageBaseApi}${
                                          mediaData.find(
                                            (m) =>
                                              m.id ===
                                              formik.values.coursesChildren[
                                                selected.cIdx
                                              ].childrenSubDetails[
                                                selected.sdIdx
                                              ].textFields[selected.stfIdx]
                                                .mediaId,
                                          )?.url
                                        }`}
                                        alt="Selected"
                                        style={{
                                          maxHeight: "150px",
                                          objectFit: "contain",
                                        }}
                                        className="img-fluid rounded"
                                      />
                                    </div>
                                    <div className="d-flex justify-content-center gap-3">
                                      <Button
                                        outline
                                        size="sm"
                                        onClick={() =>
                                          openMediaModal({
                                            type: "subTextField",
                                            childIdx: selected.cIdx,
                                            subIdx: selected.sdIdx,
                                            textIdx: selected.stfIdx,
                                          })
                                        }
                                      >
                                        {formik.values.coursesChildren[
                                          selected.cIdx
                                        ]?.childrenSubDetails[selected.sdIdx]
                                          ?.textFields[selected.stfIdx]?.mediaId
                                          ? "Change Image"
                                          : "Select Image"}
                                      </Button>

                                      {formik.values.coursesChildren[
                                        selected.cIdx
                                      ]?.childrenSubDetails[selected.sdIdx]
                                        ?.textFields[selected.stfIdx]
                                        ?.mediaId && (
                                        <Button
                                          size="sm"
                                          outline
                                          color="danger"
                                          onClick={() => {
                                            formik.setFieldValue(
                                              `coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.textFields.${selected.stfIdx}.mediaId`,
                                              "",
                                            );
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="p-4 border rounded text-center bg-light">
                                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                                      <h6 className="mt-2 mb-3 text-muted">
                                        No media selected
                                      </h6>
                                      <Button
                                        outline
                                        color="secondary"
                                        onClick={() =>
                                          openMediaModal({
                                            type: "subTextField",
                                            childIdx: selected.cIdx,
                                            subIdx: selected.sdIdx,
                                            textIdx: selected.stfIdx,
                                          })
                                        }
                                      >
                                        <i className="ri-image-add-line me-1"></i>{" "}
                                        Choose Media
                                      </Button>
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* <div className="form-check">
                                <Input
                                  type="checkbox"
                                  name={`coursesChildren.${selected.cIdx}.childrenSubDetails.${selected.sdIdx}.textFields.${selected.stfIdx}.isMainImage`}
                                  checked={
                                    formik.values.coursesChildren[selected.cIdx]
                                      ?.childrenSubDetails[selected.sdIdx]
                                      ?.textFields[selected.stfIdx]
                                      ?.isMainImage || false
                                  }
                                  onChange={formik.handleChange}
                                />
                                <Label check>Main Image</Label>
                              </div> */}
                            </CardBody>
                          </Card>
                        )}
                    </Col>
                  </Row>
                </Form>
              </FormikProvider>
            </CardBody>
          </Card>
        </Row>

        {/* Media Picker Modal */}
        <MediaModal
          isOpen={isMediaOpen}
          toggle={() => {
            setIsMediaOpen(false);
            setMediaTarget(null);
          }}
          selectedMedia={
            mediaTarget === "banner" ? bannerMedia : null // for textFields, we don't preselect
          }
          setSelectedMedia={handleMediaSelect} // fix: pass handleMediaSelect here
        />
      </Container>
    </div>
  );
};

export default CourseCreate;
